import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge, type BadgeVariant } from '../../components/ui/badge';
import {
  assetDisplayName,
  formatDuration,
  tileCategoryLabel,
  tileImage,
  tileImagePlaceholder,
  tileStatus,
  unavailableBanner,
  type StatusTone,
  type TileImage,
} from '../../lib/portalAssets/gridView';
import type { PortalAssetTile } from '../../types/portalAsset';

// THE ASSET GRID (spec §8) — shared by the client portal and the staff app.
// Presentational: it renders the tiles it is given and knows no endpoint.
//
// ⚠️ Written in shadcn TOKEN classes (bg-card, text-foreground, border-border),
// never slate literals: inside the portal those tokens follow the light/dark
// mode, and a literal does not (CLAUDE.md "never let a portal page take its ink
// from the base block").
//
// ⚠️ A status is carried by its WORDS; the badge colour only repeats it.

const TONE_VARIANT: Record<StatusTone, BadgeVariant> = {
  waiting: 'warning',
  approved: 'success',
  rejected: 'danger',
  working: 'info',
  failed: 'danger',
};

export function AssetImage({
  image,
  alt,
  onImageError,
  className = '',
}: {
  image: TileImage;
  alt: string;
  /** A signed URL can expire with its window; the host refetches (spec §7.2). */
  onImageError?: () => void;
  className?: string;
}) {
  if (image.kind === 'image') {
    return (
      <img
        src={image.url}
        alt={alt}
        loading="lazy"
        onError={onImageError}
        className={`h-full w-full object-cover ${className}`}
      />
    );
  }
  return (
    <div
      className={`flex h-full w-full items-center justify-center p-3 text-center text-xs text-muted-foreground ${className}`}
      data-testid={`asset-image-${image.kind}`}
    >
      {tileImagePlaceholder(image)}
    </div>
  );
}

export function AssetStatusBadge({ tile, short = false }: { tile: Pick<PortalAssetTile, 'lifecycle' | 'review'>; short?: boolean }) {
  const s = tileStatus(tile, { short });
  return <Badge variant={TONE_VARIANT[s.tone]}>{s.label}</Badge>;
}

function TileFrame({ tile, hrefFor, onOpen, children }: {
  tile: PortalAssetTile;
  hrefFor?: (t: PortalAssetTile) => string;
  onOpen?: (t: PortalAssetTile) => void;
  children: ReactNode;
}) {
  const cls =
    'block w-full overflow-hidden rounded-md border border-border bg-card text-left focus:outline-none focus:ring-2 focus:ring-ring';
  if (hrefFor) {
    return (
      <Link to={hrefFor(tile)} className={cls} aria-label={`Open ${assetDisplayName(tile)}`}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" className={cls} onClick={() => onOpen?.(tile)} aria-label={`Open ${assetDisplayName(tile)}`}>
      {children}
    </button>
  );
}

export function AssetGrid({
  tiles,
  hrefFor,
  onOpen,
  onImageError,
  accountName,
  selection,
  fill = false,
}: {
  tiles: PortalAssetTile[];
  hrefFor?: (t: PortalAssetTile) => string;
  onOpen?: (t: PortalAssetTile) => void;
  onImageError?: () => void;
  /** When several accounts are in view, each tile names its own. */
  accountName?: (accountId: string) => string | null;
  /**
   * STAFF BULK SELECT (2026-10-13): when given, each tile gets a checkbox. Absent on the client portal, which
   * has no bulk. The box is a SIBLING of the open control (a checkbox inside a button/link is invalid), and
   * clicking it never opens the asset.
   */
  selection?: { isSelected: (id: string) => boolean; onToggle: (id: string) => void };
  /**
   * KYCK VIEWS ONLY (Run F, 2026-10-13): add columns until the row is full instead of the 2/3/4 ladder, which
   * on an ultrawide is four enormous tiles. The 160px minimum is the staff `lg` tile width (about 167px:
   * 1024px less sidebar and padding, over four columns), so nothing gets smaller than today. The club page
   * leaves this off and keeps its ladder.
   */
  fill?: boolean;
}) {
  const banner = unavailableBanner(tiles);
  return (
    <div className="space-y-3">
      {banner && (
        <p role="status" className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground" data-testid="asset-unavailable-banner">
          {banner}
        </p>
      )}
      <ul
        className={fill ? 'grid gap-3' : 'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4'}
        style={fill ? { gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' } : undefined}
        data-testid="asset-grid"
      >
        {tiles.map((t) => {
          const duration = t.mediaType === 'video' ? formatDuration(t.durationMs) : null;
          const club = accountName?.(t.accountId) ?? null;
          return (
            <li key={t.id} className="relative">
              {selection && (
                <input
                  type="checkbox"
                  className="absolute left-2 top-2 z-10 h-5 w-5 cursor-pointer accent-primary"
                  checked={selection.isSelected(t.id)}
                  onChange={() => selection.onToggle(t.id)}
                  aria-label={`Select ${assetDisplayName(t)}`}
                />
              )}
              <TileFrame tile={t} hrefFor={hrefFor} onOpen={onOpen}>
                <div className="relative aspect-square bg-muted">
                  <AssetImage image={tileImage(t)} alt={assetDisplayName(t)} onImageError={onImageError} />
                  {t.mediaType === 'video' && (
                    <span className="absolute bottom-1 right-1 rounded-sm bg-background px-1.5 py-0.5 text-xs font-medium text-foreground">
                      ▶ {duration ?? 'Video'}
                    </span>
                  )}
                </div>
                <div className="space-y-1 p-2">
                  {/* Title first, then a short approval state, then the category (Run F). */}
                  <p className="truncate text-sm font-medium text-foreground" title={assetDisplayName(t)} data-testid="tile-name">
                    {assetDisplayName(t)}
                  </p>
                  <AssetStatusBadge tile={t} short />
                  {/* ⚠️ ONE fixed-height line, always rendered: an asset in four categories shows the first plus a
                      count, so the tile never reflows and an uncategorised one lines up with its neighbours. */}
                  <p className="h-4 truncate text-xs text-muted-foreground" title={t.categories.map((c) => c.name).join(', ')} data-testid="tile-category">
                    {tileCategoryLabel(t.categories)}
                  </p>
                  {club && <p className="truncate text-xs text-muted-foreground" title={club}>{club}</p>}
                </div>
              </TileFrame>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
